/**
 * Client-facing card (dashboard): "il tuo link per inviarci i video".
 * Always-valid personal link, no size limits. Copy / open buttons.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { copyText } from "../lib/clipboard";
import { UploadCloud, Copy, ArrowUpRight } from "lucide-react";

export default function ClientUploadLinkCard() {
  const [link, setLink] = useState(null); // { token, url }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/client/upload-link");
        setLink(data);
      } catch {
        /* non-blocking */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyLink = async () => {
    if (!link?.url) return;
    const ok = await copyText(link.url);
    if (ok) toast.success("Link copiato ✓");
    else window.prompt("Copia il link:", link.url);
  };

  if (loading || !link?.url) return null;

  return (
    <section
      data-testid="client-own-upload-link-card"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-11 w-11 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] grid place-items-center shrink-0 border-2 border-[var(--mp-ink)]">
          <UploadCloud className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            Il tuo link per inviarci i video
          </div>
          <div className="font-display text-xl sm:text-2xl tracking-tight mt-0.5">
            Carica i video quando vuoi, senza limiti
          </div>
          <p className="text-sm text-[var(--mp-muted)] mt-1 leading-relaxed max-w-xl">
            Questo link è personale e vale per sempre: aprilo dal telefono,
            scegli i video (anche molto grandi) e arrivano dritti a noi.
            Salvalo tra i preferiti o fissalo nella chat Telegram.
          </p>
        </div>
      </div>
      <div
        data-testid="client-own-upload-link-url"
        className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] px-4 py-3 font-mono text-xs break-all select-all"
      >
        {link.url}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button
          data-testid="client-own-upload-link-copy-btn"
          onClick={copyLink}
          className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
        >
          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copia link
        </Button>
        <Button
          data-testid="client-own-upload-link-open-btn"
          onClick={() => window.open(link.url, "_blank", "noopener")}
          variant="outline"
          className="rounded-full border-[var(--mp-ink)]"
        >
          <ArrowUpRight className="h-3.5 w-3.5 mr-1.5" /> Apri pagina upload
        </Button>
      </div>
    </section>
  );
}
